import React from "react";
import { Link } from "react-router-dom";
import { ContactEmail, PolicyPage, PolicySection } from "./PolicyPage";

function Terms() {
  return (
    <PolicyPage
      title="Terms of Service"
      intro="These Terms govern access to sacuddin.shop and purchases from Sacuddin. By using the site or placing an order, you agree to these Terms and the policies linked below."
    >
      <p className="policy-notice">
        Nothing in these Terms excludes or limits a right or remedy that cannot
        lawfully be excluded under the consumer law that applies to you.
      </p>

      <PolicySection title="1. About these Terms">
        <p>
          “Sacuddin,” “we,” “us,” and “our” refer to the operator of sacuddin.shop.
          “You” means a visitor or customer. These Terms incorporate our Privacy,
          Shipping, Returns, Payment, Order, Acceptable Use, and other policies
          displayed in the <Link to="/policies">Policy Center</Link>.
        </p>
        <p>
          You must be legally capable of entering a binding contract, or use the site
          with the involvement of a parent or legal guardian. The site is not directed
          to children under 16.
        </p>
      </PolicySection>

      <PolicySection title="2. Site access and accounts">
        <p>
          You may browse for personal, non-commercial shopping purposes. If account
          features are made available, you are responsible for accurate information,
          protecting credentials, and activity under your account. Tell us promptly if
          you suspect unauthorized use. We may refuse, restrict, or end access where
          reasonably necessary for security, legal compliance, or a breach of these
          Terms.
        </p>
      </PolicySection>

      <PolicySection title="3. Product information">
        <p>
          We aim to present descriptions, dimensions, materials, colors, and images
          accurately. Screen settings, photography, hand-finishing, and natural
          materials can produce reasonable variation. Product details on the product
          page at the time of purchase form part of your order. See our <Link to="/product-policy">Product Information Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="4. Prices, taxes, and errors">
        <p>
          Prices are shown in the currency indicated on the site and may change before
          an order is accepted. Delivery charges, duties, and applicable taxes may be
          added at checkout or collected by authorities or carriers. If a price,
          description, promotion, or availability error is discovered, we may correct
          it and ask whether you wish to proceed, or cancel and refund the affected
          amount. We will not charge a higher corrected price without your agreement.
        </p>
      </PolicySection>

      <PolicySection title="5. Orders and acceptance">
        <p>
          Adding an item to a cart does not reserve it. Your checkout submission is an
          offer to purchase. An automated acknowledgment confirms receipt, not final
          acceptance. A contract is formed when we send an acceptance or dispatch
          confirmation, depending on the checkout flow available to you.
        </p>
        <p>
          We may decline or cancel an order before acceptance for reasons including
          unavailability, payment failure, suspected fraud, shipping restrictions,
          legal requirements, obvious errors, or purchase limits. If payment has been
          taken, we will refund the cancelled amount to the original payment method.
          More details appear in our <Link to="/orders">Order &amp; Cancellation Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="6. Payment">
        <p>
          You authorize the displayed order total and confirm that you are permitted to
          use the payment method supplied. Payment may be authorized when an order is
          submitted and captured at that time or later. Your issuer or payment provider
          may apply its own terms, currency conversion, or fees. See our <Link to="/payments">Payment &amp; Billing Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="7. Shipping, risk, and delivery">
        <p>
          Available methods, estimates, and charges are shown at checkout where
          possible. Estimates are not guaranteed unless expressly stated. You are
          responsible for providing a complete, accurate, and deliverable address.
          Title and risk transfer as required by applicable law; nothing in these Terms
          shifts carrier risk to a consumer earlier than the law permits. See our <Link to="/shipping">Shipping &amp; Delivery Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="8. Returns, refunds, and warranty">
        <p>
          Eligibility, time limits, condition requirements, exclusions, and refund
          timing are described in our <Link to="/returns">Returns &amp; Refunds Policy</Link>.
          Product care and defect coverage are explained in our <Link to="/warranty-care">Jewelry Care &amp; Limited Warranty</Link>. These policies do not replace mandatory statutory rights.
        </p>
      </PolicySection>

      <PolicySection title="9. Promotions and gift cards">
        <p>
          Offers are subject to their stated period, eligibility, exclusions, and stock.
          Unless stated otherwise, promotions cannot be combined, applied to past
          purchases, exchanged for cash, or used contrary to law. Gift-card terms apply
          only if gift cards are offered. See our <Link to="/promotions">Promotions, Discounts &amp; Gift Cards Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="10. Intellectual property">
        <p>
          The site and Sacuddin-created text, layout, graphics, photography, product
          presentation, logos, and software are protected by applicable intellectual
          property laws. You may use the site for personal shopping, but may not copy,
          scrape, republish, sell, reverse engineer, or create derivative commercial
          materials without permission or a lawful exception. See our <Link to="/intellectual-property">Intellectual Property Notice</Link>.
        </p>
      </PolicySection>

      <PolicySection title="11. Your content and feedback">
        <p>
          You retain ownership of content you submit. When you submit a review, image,
          comment, or other content for publication, you give Sacuddin a non-exclusive,
          worldwide, royalty-free license to host, reproduce, format, and display it for
          operating, improving, and promoting the store, for as long as the content is
          used, subject to applicable law. You confirm you have the necessary rights and
          that the content is accurate, lawful, and does not infringe another person’s
          rights. We may moderate or remove content in line with our <Link to="/acceptable-use">Acceptable Use Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="12. Third-party services and links">
        <p>
          Checkout, payment, delivery, maps, social features, and other functions may be
          supplied by third parties. Their separate terms and privacy notices may apply.
          A link does not mean that Sacuddin endorses or controls the destination. We are
          not responsible for third-party acts except where the law provides otherwise.
        </p>
      </PolicySection>

      <PolicySection title="13. Availability and changes">
        <p>
          We may maintain, update, or discontinue site features and may correct content
          without notice. We do not promise uninterrupted or error-free access. We will
          not make a retroactive change that materially reduces an accepted order right
          unless required by law or agreed with you.
        </p>
      </PolicySection>

      <PolicySection title="14. Disclaimers">
        <p>
          To the maximum extent permitted by law, the site is provided “as available.”
          We disclaim implied warranties relating to site availability or content, but
          not warranties or guarantees that cannot lawfully be excluded. Jewelry care,
          allergy, sizing, and product-specific information should be reviewed before
          purchase and use.
        </p>
      </PolicySection>

      <PolicySection title="15. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Sacuddin is not liable for indirect,
          incidental, special, exemplary, or consequential loss that was not reasonably
          foreseeable when a contract was formed. Where liability may lawfully be
          limited, our aggregate liability for a claim connected to a product order will
          not exceed the amount paid for the affected order.
        </p>
        <p>
          These limits do not apply to fraud, willful misconduct, death or personal
          injury caused by negligence, breach of mandatory consumer guarantees, or any
          liability that applicable law does not allow us to exclude or limit.
        </p>
      </PolicySection>

      <PolicySection title="16. Your responsibility">
        <p>
          You are responsible for losses reasonably caused by your unlawful use of the
          site, breach of these Terms, or infringement of another person’s rights. This
          does not require a consumer to indemnify Sacuddin beyond what applicable law
          permits.
        </p>
      </PolicySection>

      <PolicySection title="17. Resolving concerns">
        <p>
          Please first contact <ContactEmail subject="Order or policy concern" /> with
          the relevant details so we can try to resolve the issue. You and Sacuddin may
          pursue any remedy available under applicable law. These Terms do not require
          arbitration, waive a class remedy, or restrict access to a regulator or court
          where such a restriction would be invalid.
        </p>
      </PolicySection>

      <PolicySection title="18. Applicable law">
        <p>
          These Terms are governed by the laws that apply to the relevant transaction
          and the operation of the site, without depriving you of mandatory protections
          in your place of residence. Disputes may be brought before a court or forum
          with jurisdiction under applicable law.
        </p>
      </PolicySection>

      <PolicySection title="19. General terms">
        <p>
          If a provision is unenforceable, it will be limited or removed only to the
          extent necessary and the remaining provisions continue. A delay in enforcing
          a right is not a waiver. You may not transfer an accepted order or these Terms
          without our consent; we may transfer our rights and obligations as part of a
          business reorganization or sale, provided your rights are not reduced.
        </p>
      </PolicySection>

      <PolicySection title="20. Changes and contact">
        <p>
          Revised Terms apply from the posted “Last updated” date. The version in effect
          when we accept an order generally governs that order. Questions may be sent to
          <ContactEmail subject="Terms of Service question" />.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export default Terms;
